import { useMemo } from 'react'
import Chart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'
import { baseOptions } from './lib/chartTheme'
import { changeColor, trendColor } from './lib/series'
import { useChartPalette } from './lib/useChartPalette'

type SparklineProps = {
  categories: string[]
  data: (number | null)[]
  name: string
  /** The chart's text alternative; see `chartSummary`. */
  label: string
  height?: number
  /** A fall is good news: the line is green when the last movement fell. */
  lowerIsBetter?: boolean
  /** The card's change, which colours the line as its badge is (else the last movement does). */
  change?: number | null | undefined
}

/**
 * Bare trend thumbnail: a smooth monotone line with a soft gradient fill,
 * coloured by the last movement (green if the last point rose, red if it fell;
 * the other way round where lower is better).
 */
export const Sparkline = ({
  categories,
  data,
  name,
  label,
  height = 92,
  lowerIsBetter = false,
  change,
}: SparklineProps) => {
  const { palette } = useChartPalette()

  const values = useMemo(
    () => data.filter((v): v is number => v !== null),
    [data]
  )
  const series = useMemo(() => [{ name, data }], [name, data])
  const options = useMemo((): ApexOptions | null => {
    // Nothing to draw, and no range to give the axis.
    if (values.length === 0) return null
    const base = baseOptions(palette)
    const min = Math.min(...values)
    const max = Math.max(...values)
    return {
      ...base,
      chart: {
        ...base.chart,
        type: 'area',
        sparkline: { enabled: true },
        zoom: { enabled: false },
        selection: { enabled: false },
        animations: { enabled: false },
      },
      colors: [
        changeColor(change, palette, lowerIsBetter) ??
          trendColor(values, palette, lowerIsBetter),
      ],
      stroke: { curve: 'monotoneCubic', width: 2 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 0,
          opacityFrom: 0.32,
          opacityTo: 0,
          stops: [0, 100],
        },
      },
      plotOptions: { area: { fillTo: 'end' } },
      markers: { size: 0 },
      grid: { padding: { left: 0, right: 0, bottom: 0, top: 2 } },
      xaxis: { categories },
      // The range can't be empty (a flat series) or Apex's scale loops.
      yaxis: {
        min: min - (max - min) * 0.15,
        max: max > min ? max : min + 1,
      },
      tooltip: { enabled: false },
    }
  }, [values, palette, categories, lowerIsBetter, change])

  if (!options) return null

  return (
    <div role='img' aria-label={label}>
      <Chart
        type='area'
        height={height}
        width='100%'
        options={options}
        series={series}
      />
    </div>
  )
}
